import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Stack, Typography, useTheme, useMediaQuery } from "@mui/material";
import { useLocation } from "react-router-dom";

import actions from "actions";
import {
  getClinicianClientStatusInfoLoading,
  getClinicianWorkflowStatus,
  getUpdateUserInsuranceStatusLoading,
  getUser,
  getUserId,
  getUserClinicianId,
  getClinicianCalendarSynced,
} from "selectors";
import PageContainer from "elements/PageContainer";
import { useGetClinicianBasicInfo } from "hooks/api/clinician/useGetClinicianBasicInfo";
import useGetClinicianClients from "hooks/api/clinician/useGetClinicianClients";
import { useGetFeatureFlag } from "hooks/useGetFeatureFlag";
import CalendarSync from "components/CalendarSync";

// Modules
import SummaryWeek from "./SummaryWeek";
import NextAppointment from "./NextAppointment/";
import DeliveredHours from "./DeliveredHours";
import AuthSchedDelivered from "./AuthSchedDelivered";
import CancellationsNoShows from "./CancellationsNoShows";
import CallsNotStartedHandler from "../CallNotStartedHandler";

export default function ClinicianDashboardContainer() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const location = useLocation();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  // Local state for CalendarSync dialog
  const [calendarSyncOpen, setCalendarSyncOpen] = useState(false);

  // Redux Selectors
  const clinicianWorkflowStatus = useSelector(getClinicianWorkflowStatus);
  const clinician = useSelector(getUser);
  const userId = useSelector(getUserId);
  const clinicianId = useSelector(getUserClinicianId);
  const calendarSynced = useSelector(getClinicianCalendarSynced);
  const clientStatusInfoLoading = useSelector(getClinicianClientStatusInfoLoading);
  const updateUserInsuranceStatusLoading = useSelector(getUpdateUserInsuranceStatusLoading);

  // React Query Hooks
  const { clinicianBasicInfo, clinicianBasicInfoLoading } = useGetClinicianBasicInfo(
    clinician?.clinician_id
  );

  const { clinicianClients, clinicianClientsLoading } = useGetClinicianClients(clinician?.id, {
    size: 100,
  });

  const { data: onSchedFeatureFlag, isLoading: featureFlagLoading } = useGetFeatureFlag(
    userId,
    "onsched_setup"
  );

  useEffect(() => {
    dispatch(
      actions.setPageDetails({
        pageName: "Home",
        altTitle: " ",
        currentPage: "dashboard",
        menu: "clinicianHome",
        hideDivider: true,
      })
    );
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const justSynced = params.get("calendarSyncRedirect");

    if (
      clinicianWorkflowStatus &&
      !calendarSynced &&
      onSchedFeatureFlag &&
      !featureFlagLoading &&
      !justSynced
    ) {
      setCalendarSyncOpen(true);
    }
  }, [
    clinicianWorkflowStatus,
    calendarSynced,
    onSchedFeatureFlag,
    featureFlagLoading,
    location.search,
  ]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("calendarSyncRedirect") && clinicianId) {
      dispatch(actions.setSuccessfulCalendarSync(clinicianId));
    }
  }, [dispatch, location.search, clinicianId]);

  const handleCloseCalendarSync = () => {
    setCalendarSyncOpen(false);
  };

  const loading =
    !clinicianWorkflowStatus ||
    clientStatusInfoLoading ||
    updateUserInsuranceStatusLoading ||
    clinicianBasicInfoLoading ||
    clinicianClientsLoading ||
    featureFlagLoading;

  return (
    <PageContainer loading={loading}>
      <Stack gap={7}>
        <Box id="clinician-dashboard-header">
          <Typography component="p" variant="body2" color="text.secondary">
            {new Date().toDateString()}
          </Typography>

          <Typography
            component="h2"
            variant="h2"
            fontFamily="TestTiemposText"
            fontWeight="500"
            color="#6C3693"
          >
            {clinician?.first_name && `Hey there, ${clinician.first_name} 👋`}
          </Typography>
        </Box>

        <Stack
          id="clinician-dashboard-content"
          gap={7}
          direction={{ xs: "column", lg: "row" }}
          flexWrap="wrap"
        >
          <SummaryWeek
            clinicianId={clinician?.clinician_id}
            employmentType={clinicianBasicInfo?.employment_type}
            isSmallScreen={isSmallScreen}
          />

          <NextAppointment
            clinicianUserId={clinician?.id}
            clinicianInfo={clinicianBasicInfo}
            isSmallScreen={isSmallScreen}
          />
        </Stack>

        <DeliveredHours
          clinicianId={clinician?.clinician_id}
          clients={clinicianClients}
          isSmallScreen={isSmallScreen}
        />

        <AuthSchedDelivered clients={clinicianClients} isSmallScreen={isSmallScreen} />

        <CancellationsNoShows
          clinicianId={clinician?.clinician_id}
          clients={clinicianClients}
          isSmallScreen={isSmallScreen}
        />

        {clinicianId && onSchedFeatureFlag && (
          <CalendarSync open={calendarSyncOpen} onClose={handleCloseCalendarSync} />
        )}
        <CallsNotStartedHandler clinicianId={clinician?.clinician_id} />
      </Stack>
    </PageContainer>
  );
}
